import { useState, useEffect, useCallback } from 'react'
import { DollarSign, Users, CheckCircle, Clock, RefreshCw } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { KpiCard } from './analytics/KpiCard'
import { RevenueLineChart } from './analytics/RevenueLineChart'
import { CustomerDonutChart } from './analytics/CustomerDonutChart'
import { VerificationAreaChart } from './analytics/VerificationAreaChart'
import {
  getExecutiveSummary,
  getRevenueTrend,
  getCustomerDistribution,
  getVerificationVolume,
  getTopBusinesses,
} from '@/src/lib/api/admin-analytics'
import type {
  ExecutiveSummary,
  RevenueTrendPoint,
  CustomerDistributionItem,
  VerificationVolumePoint,
  TopBusiness,
} from '@/src/types/admin/analytics'

const REFRESH_MS = 30 * 60 * 1000 // 30 minutes

export default function AdminDashboard() {
  const [summary, setSummary] = useState<ExecutiveSummary | null>(null)
  const [revenueTrend, setRevenueTrend] = useState<RevenueTrendPoint[]>([])
  const [distribution, setDistribution] = useState<CustomerDistributionItem[]>([])
  const [verificationVol, setVerificationVol] = useState<VerificationVolumePoint[]>([])
  const [topBusinesses, setTopBusinesses] = useState<TopBusiness[]>([])
  const [loading, setLoading] = useState(true)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [sum, rev, dist, ver, top] = await Promise.allSettled([
        getExecutiveSummary(),
        getRevenueTrend(12),
        getCustomerDistribution(),
        getVerificationVolume(12),
        getTopBusinesses(10),
      ])
      if (sum.status === 'fulfilled') setSummary(sum.value)
      else console.error('[Dashboard] executive-summary error:', sum.reason)
      if (rev.status === 'fulfilled') setRevenueTrend(rev.value)
      else console.error('[Dashboard] revenue-trend error:', rev.reason)
      if (dist.status === 'fulfilled') setDistribution(dist.value)
      else console.error('[Dashboard] customer-distribution error:', dist.reason)
      if (ver.status === 'fulfilled') setVerificationVol(ver.value)
      else console.error('[Dashboard] verification-volume error:', ver.reason)
      if (top.status === 'fulfilled') setTopBusinesses(top.value)
      else console.error('[Dashboard] top-businesses error:', top.reason)
      setLastUpdated(new Date())
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAll()
    const id = setInterval(fetchAll, REFRESH_MS)
    return () => clearInterval(id)
  }, [fetchAll])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">ภาพรวมผู้บริหาร</h1>
          <p className="text-muted-foreground">ภาพรวมรายได้ ลูกค้า และการยืนยันสลิป</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-muted-foreground">
              อัพเดตล่าสุด {lastUpdated.toLocaleTimeString('th-TH')}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            รีเฟรช
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <KpiCard
          title="รายได้เดือนนี้"
          value={summary ? `฿${(summary.revenue_mtd / 1000).toFixed(0)}K` : '—'}
          change={summary?.revenue_growth_pct ?? null}
          icon={DollarSign}
          loading={loading && !summary}
        />
        <KpiCard
          title="ลูกค้าใช้งาน"
          value={summary?.active_customers ?? '—'}
          change={summary?.customer_growth_pct ?? null}
          icon={Users}
          loading={loading && !summary}
        />
        <KpiCard
          title="ลูกค้าใหม่เดือนนี้"
          value={summary?.new_customers_this_month ?? '—'}
          icon={Users}
          loading={loading && !summary}
        />
        <KpiCard
          title="การยืนยันวันนี้"
          value={summary?.verifications_today ?? '—'}
          change={summary?.verification_change_pct ?? null}
          icon={CheckCircle}
          loading={loading && !summary}
        />
        <KpiCard
          title="รออนุมัติ"
          value={summary?.pending_total ?? '—'}
          icon={Clock}
          loading={loading && !summary}
          invertTrend
        />
      </div>

      {/* Charts — Row 1 */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">รายได้รายเดือน (12 เดือน)</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && revenueTrend.length === 0 ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (
              <RevenueLineChart data={revenueTrend} height={300} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">การยืนยันสลิปรายเดือน</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && verificationVol.length === 0 ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (
              <VerificationAreaChart data={verificationVol} height={300} />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts — Row 2 */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">สัดส่วนลูกค้าตามแพ็กเกจ</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && distribution.length === 0 ? (
              <Skeleton className="h-[280px] w-full" />
            ) : (
              <CustomerDonutChart data={distribution} height={280} />
            )}
          </CardContent>
        </Card>

        {/* Top Businesses Table */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Top 10 ธุรกิจ (30 วันล่าสุด)</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && topBusinesses.length === 0 ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ธุรกิจ</TableHead>
                    <TableHead>แพ็กเกจ</TableHead>
                    <TableHead className="text-right">การยืนยัน</TableHead>
                    <TableHead className="text-right">เครดิตที่ใช้</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topBusinesses.map((b) => (
                    <TableRow key={b.business_id}>
                      <TableCell>
                        <div className="font-medium truncate max-w-[160px]">{b.business_name}</div>
                        <div className="text-xs text-muted-foreground truncate max-w-[160px]">
                          {b.owner_email}
                        </div>
                      </TableCell>
                      <TableCell>{b.package_code}</TableCell>
                      <TableCell className="text-right">{b.verifications.toLocaleString()}</TableCell>
                      <TableCell className="text-right">
                        {b.credits_used.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
